import React, { useState } from "react";
import "../../../css/Common/Components/ContactUs/ContactUs.css";
import "../../../css/Common/Components/ContactUs/RequestCallback.css"; // Include your custom styles
import { db } from "../../../backend/firebaseConfig";
import { addDoc, collection } from "firebase/firestore";
import NotificationPopup from "./PopUp";
import InputField from "../DesignSystem/InputField";
import CheckBox from "../DesignSystem/Buttons/CheckBox";
import ButtonYellow from "../DesignSystem/Buttons/ButtonYellow";
import TimeSelector from "./TimeSelector";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import notifyRenderError from "frontend/Utils/notifyError";

/**
 * RequestCallback component renders a form for users to request a callback from a financial adviser.
 * 
 * Features:
 * - Collects user details such as name, phone number, email, message, town/city, preferred date and time, and interested services.
 * - Validates form fields and displays error messages for invalid or missing input.
 * - Submits the form data to a Firestore collection and sends a confirmation email.
 * - Displays a notification popup upon successful submission.
 * - Allows users to select services and preferred callback time.
 * - Enforces acceptance of the privacy policy before submission.
 *
 */
export const RequestCallback = ({ title = "", onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    message: "",
    town: "",
    services: [],
    date: "",
    time: "",
    role: "Customer",
    privacyPolicy: false,
  });

  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false); // Popup visibility
  const [popupMessage, setPopupMessage] = useState(""); // Popup message
  const [popupType, setPopupType] = useState("success"); // Popup type
  const [showTimeSelector, setShowTimeSelector] = useState(false);
  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}/${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${today.getFullYear()}`;

  /**
   * Validates the contact form data and returns an object containing any validation errors.
   *
   * @returns {Object} An object where each key corresponds to a form field with a validation error,
   *                   and the value is the associated error message.
   *
   * Validation rules:
   * - Name is required.
   * - Phone number is required.
   * - If the form title is "Request a Callback", message is required.
   * - If email is provided, it must be valid.
   * - Preferred date is required.
   * - At least one service must be selected.
   * - Preferred time is required.
   * - Privacy policy must be accepted.
   */
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors["name"] = "Name is required.";
    if (!formData.phoneNumber.trim())
      newErrors["phoneNumber"] = "Phone number is required.";
    if (title === "Request a Callback") {
      if (!formData.message.trim())
        newErrors["message"] = "Message is required.";
    }

    if (formData.email.trim() && !isValidEmail(formData.email))
      newErrors["email"] = "Please enter a valid email address.";

    if (!formData.date) {
      newErrors["date"] = "Preferred date is required.";
    }

    // if (!formData.town.trim()) newErrors["town"] = "Town/City is required.";

    if (formData.services.length === 0) {
      newErrors["services"] = "Please select at least one service.";
    }

    if (!formData.time) newErrors["time"] = "Preferred time is required.";
    if (formData.privacyPolicy === false)
      newErrors["privacyPolicy"] = "You must accept the privacy policy.";
    // newErrors["time"] = "Preferred time is required.";
    return newErrors;
  };

  /**
   * Handles changes to input fields in the contact form.
   * Updates the form data state based on the input type and value.
   * - For checkboxes named "services", it adds or removes the service from the array.
   * - For other checkboxes, it updates the boolean value.
   * - For other input types, it updates the value directly.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "services") {
      setFormData((prevData) => ({
        ...prevData,
        services: checked
          ? [...prevData.services, value]
          : prevData.services.filter((service) => service !== value),
      }));
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
    // validateForm();
  };

  /**
   * Sends a confirmation email for a request callback using the provided form data.
   *
   * Makes a POST request to the backend API endpoint to trigger an email confirmation
   * for the callback request. The function extracts and formats the necessary fields
   * from the form data before sending.
   *
   * @async
   * @function
   * @param {Object} params - The parameters object.
   * @param {Object} params.formData - The form data containing user input.
   * @param {string} params.formData.name - The full name of the user.
   * @param {string} params.formData.date - The date of the callback in 'YYYY-MM-DD' format.
   * @param {string} params.formData.time - The time of the callback.
   * @param {string} params.formData.email - The user's email address.
   * @returns {Promise<void>} Resolves when the request is complete.
   */
  const sendMailConfirmation = async ({ formData }) => {

    try {
      const response = await fetch(
        "https://rosemount-financial-solution-ifa-ltd.onrender.com/send-request-callback-confirmation",
        // "http://localhost:5000/send-request-callback-confirmation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name.trim().split(" ")[0],
            date: formData.date.split("-").reverse().join("-"),
            time: formData.time,
            email: formData.email.trim(),
          }),
        }
      );
      const result = await response.json();
      // console.log(result);
      if (response.ok) {
      } else {
        // console.error("Error:", result);
      }
    } catch (error) {
      try {
        await notifyRenderError(error, "NETWORK_ERROR");
      } catch (e) {}
    }

    // handleClosePopup();
    // setShowPopup(true);
  };

  /**
   * Handles the submission of the contact form.
   * 
   * Validates the form data, displays errors if validation fails,
   * and if successful, submits the enquiry to the Firestore database.
   * On successful submission, shows a success popup, sends a confirmation email,
   * and resets the form fields.
   * 
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the submission process is complete.
   */
  const handleSubmit = async () => {
    const validationErrors = validateForm();
    // console.log(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await addDoc(collection(db, "Enquiries"), {
        ...formData,
        createdAt: formattedDate,
      });
      const formattedMessage = `Thanks for submitting your details. One of our advisers will be in touch on ${formData.date
        .split("-")
        .reverse()
        .join("-")} at ${formData.time}.`;
      // console.log(formattedMessage);
      setPopupType("success");
      setPopupMessage(formattedMessage);
      setShowPopup(true); // Show success popup

      sendMailConfirmation({ formData });

      setFormData({
        name: "",
        phoneNumber: "",
        email: "",
        message: "",
        services: [],
        town: "",
        date: "",
        time: "",
        role: "Customer",
        privacyPolicy: false,
      });
    } catch (error) {
      // console.error("Error submitting request:", error);
    }
  };


  /**
   * Validates whether the provided string is a valid email address.
   *
   * This function uses a comprehensive regular expression to check if the input
   * string conforms to the general structure of an email address, including
   * support for quoted strings, domain literals, and subdomains.
   *
   * @param {string} email - The email address to validate.
   * @returns {boolean} Returns true if the email is valid, false otherwise.
   */
  const isValidEmail = (email) => {
    const emailRegex =
      /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    // console.log(emailRegex.test(email));
    return emailRegex.test(email);
  };

  /**
   * Handles the closing of the popup by invoking the provided onClose callback.
   * Typically used to close modal dialogs or popups in the UI.
   *
   * @function
   * @returns {void}
   */
  const handleClosePopup = () => {
    onClose();
  };

  /**
   * Calculates and returns the earliest valid start date, which is at least two days from today,
   * skipping weekends (Saturday and Sunday). If the calculated date falls on a weekend,
   * it advances to the next Monday.
   *
   * @returns {string} The earliest valid start date in 'YYYY-MM-DD' format.
   */
  const getValidStartDate = () => {
    const today = new Date();
    let date = new Date(today.setDate(today.getDate() + 2)); // Start 2 days ahead

    // Exclude weekends
    while (date.getDay() === 0 || date.getDay() === 6) {
      date.setDate(date.getDate() + 1);
    }

    // Return the formatted date (YYYY-MM-DD)
    return date.toISOString().split("T")[0];
  };

  /**
   * Displays the TimeSelector dropdown by setting the showTimeSelector state to true.
   * Typically used as an event handler to allow users to select a time for callback requests.
   */
  const handleShowTimeSelector = () => {
    setShowTimeSelector(true); // Show TimeSelector dropdown
  };

  /**
   * Handles the closing of the TimeSelector dropdown.
   * Sets the state to hide the TimeSelector component.
   *
   * @function
   * @returns {void}
   */
  const handleCloseTimeSelector = () => {
    setShowTimeSelector(false); // Hide TimeSelector dropdown
  };



  /**
   * Handles changes to form input fields and updates the form data state.
   *
   * Updates the corresponding field in the form data state.
   * If the input type is a checkbox, uses the checked property; otherwise, uses the value property.
   */
  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    // console.log(formData);
    // validateForm();
  };

  /**
   * Handles the change event for service checkboxes in the contact form.
   * Updates the form data to add or remove the selected service based on the checkbox state.
   * Also updates the error state to remove the "services" error if a service is selected.
   *
   * @param {Object} event - The event object from the checkbox input.
   * @param {string} service - The name or identifier of the service being toggled.
   */
  const handleServiceChange = (event, service) => {
    const { checked } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      services: checked
        ? [...prevData.services, service] // Add service if checked
        : prevData.services.filter((s) => s !== service), // Remove service if unchecked
    }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (updatedErrors["services"] !== "" && checked) {
        delete updatedErrors["services"]; // Remove services error if resolved
      }
      return updatedErrors;
    });
  };

  return (
    <>
      <form className="request-callback-widget" onSubmit={handleSubmit}>
        <div className="close-icon Content" onClick={handleClosePopup}>
          <CloseIcon />
        </div>
        <div className="request-callback-form-container">
          <div className="form-container">
            <h2 id="callback">{title ? title : "Request a Callback"}</h2>
            <div className="Content">
              Fill out the form and a financial adviser will be in contact with
              you within 2 working days.
            </div>
            <InputField
              title="Name*"
              placeholder="Name"
              name="name"
              value={formData.name.trim() ? formData.name : ""}
              onChange={handleChange}
              required={errors["name"] ? true : false}
              isTextarea={false}
            />
            <InputField
              title="Phone Number*"
              placeholder="01234567890"
              name="phoneNumber"
              required={errors["phoneNumber"] ? true : false}
              isTextarea={false}
              value={formData.phoneNumber.trim() ? formData.phoneNumber : ""}
              onChange={handleChange}
            />
            <InputField
              title="Email"
              placeholder="name@example.com"
              name="email"
              required={errors["email"] ? true : false}
              value={formData.email.trim() ? formData.email : ""}
              onChange={handleChange}
              isTextarea={false}
            />
            {errors["email"] === "Please enter a valid email address." && (
              <div className="Error-Message">{errors["email"]}</div>
            )}
            <InputField
              title="Town/City"
              placeholder="Enter Your Town or City"
              name="town"
              // required={errors["town"] ? true : false}
              isTextarea={false}
              value={formData.town.trim() ? formData.town : ""}
              onChange={handleChange}
            />
            <InputField
              title={title === "Request a Callback" ? "Message*" : "Message"}
              placeholder="Message"
              name="message"
              required={errors["message"] ? true : false}
              isTextarea={true}
              value={formData.message.trim() ? formData.message : ""}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="request-callback-info-container">
          <div className="services">
            <div className="Content">
              Please choose the services you are interested in:
            </div>
            <div className="services-grid">
              {[
                "Mortgage Planning",
                "Investment Planning",
                "Wealth Management",
                "Retirement Planning",
                "Insurance/ Protection",
                "Corporate Planning",
              ].map((service) => (
                <div className="service-option" key={service}>
                  <CheckBox
                    error={
                      formData.services.length === 0 && errors["services"]
                        ? true
                        : false
                    }
                    name="services"
                    value={service} // Pass the service name as value
                    checked={formData.services.includes(service)} // Check if service is selected
                    onChange={(e) => handleServiceChange(e, service)} // Handle selection
                  />
                  <div className="Content">{service}</div>
                </div>
              ))}
            </div>
          </div>
          {/* <span>{errors["services"]}</span> */}
          <InputField
            // onClick={(e) => e.target.showPicker()}
            onClick={(e) => {
              if (e.target.showPicker) e.target.showPicker();
            }}
            min={getValidStartDate()}
            specialType="date"
            title="Preferred Date*"
            name="date"
            value={formData.date}
            required={errors["date"] ? true : false}
            onChange={handleChange}
          />

          <div className="row">
            <div className="Label-Medium">Preferred Time*</div>
            <input
              // onChange={handleChange}
              name="time"
              value={formData.time}
              className={`row-textfield ${errors["time"] ? "error" : ""}`}
              placeholder="Select a Time"
              required={errors["time"] ? true : false}
              onClick={handleShowTimeSelector} // Show the dropdown on click
              readOnly
            />
            {showTimeSelector && (
              <TimeSelector
                selectedDate={formData.date}
                selectedTime={formData.time}
                setSelectedTime={(time) =>
                  setFormData((prevData) => ({ ...prevData, time }))
                }
                onClose={handleCloseTimeSelector}
              />
            )}
          </div>
          <div className="privacy-policy-container">
            <CheckBox
              name="privacyPolicy"
              error={
                errors["privacyPolicy"] && formData.privacyPolicy === false
                  ? true
                  : false
              }
              checked={formData.privacyPolicy}
              onChange={handleInputChange}
            />
            <div className="Content-SemiBold">
              I have read and understood the{"   "}
              <span
                className="Content-Bold-Underline"
                onClick={() => {
                  navigate("/PrivacyPolicy");
                  // console.log("Privacy Policy clicked");
                }}
              >
                privacy policy
              </span>
            </div>
          </div>
          <ButtonYellow buttonText="SUBMIT" onClick={handleSubmit} />
        </div>
      </form>
      {showPopup && (
        <NotificationPopup
          message={popupMessage}
          type={popupType}
          onClose={() => setShowPopup(false)} // Close popup on click
        />
      )}
    </>
  );
};
